// Tier 0 API client — POST /session + WS /ws/stream
//
// Sprint Day 44 update (Tier B-NEW-1 partial finish gaps 1+2):
//   Gap 1: WP gateway JWT mode (production) thay direct /session call (skeleton)
//   Gap 2: Distinct error types (401/403/network/timeout) cho UI render

const SESSION_TIMEOUT_MS = 8000;
const WS_RECONNECT_DELAYS_MS = [1000, 3000, 7000];  // exponential backoff 3 retries

export class ApiError extends Error {
  constructor(kind, message, statusCode = null) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;  // 'auth' | 'network' | 'timeout' | 'server' | 'unknown'
    this.statusCode = statusCode;
  }
}

export class ApiClient {
  constructor(apiBase, wsBase, options = {}) {
    this.apiBase = apiBase;
    this.wsBase = wsBase;
    this.ws = null;
    this.session = null;
    this.callbacks = {};
    // Gap 1: WP gateway endpoint (production mode)
    // Khi set, browser fetch JWT từ WP plugin endpoint thay vì gọi direct /session
    // (KHÔNG cần expose bridge secret browser-side).
    // WP plugin verify WP user logged-in → mint engine JWT + return.
    this.wpGatewayUrl = options.wpGatewayUrl || null;
    // Reconnect state
    this._reconnectAttempt = 0;
    this._reconnectTimer = null;
    this._intentionalClose = false;
  }

  get isOpen() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Gap 1 fix Sprint Day 44 — Create session via WP gateway (production) hoặc
   * direct /session (skeleton/dev only).
   *
   * Production flow:
   *   1. Browser fetch JWT từ WP gateway POST /wp-json/peakedu/v1/session
   *      (WP plugin verify wp_user_id + mint engine JWT server-side)
   *   2. Gateway response: {access_token, trace_id, ws_url, expires_in}
   *
   * Dev flow (KHÔNG khuyến nghị production):
   *   - Direct POST {apiBase}/session với no header → engine require X-Bridge-Secret
   *   - Sẽ fail 401 trừ khi engine ENGINE_ENV=development
   */
  async createSession(text) {
    const url = this.wpGatewayUrl
      ? this.wpGatewayUrl
      : `${this.apiBase}/session`;
    const mode = this.wpGatewayUrl ? 'wp_gateway' : 'direct_skeleton';

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SESSION_TIMEOUT_MS);

    try {
      const resp = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, lang: 'en' }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (resp.status === 401 || resp.status === 403) {
        const detail = await this._safeReadError(resp);
        throw new ApiError('auth', `Auth failed (${resp.status}): ${detail}`, resp.status);
      }
      if (!resp.ok) {
        const detail = await this._safeReadError(resp);
        throw new ApiError('server', `Session create failed (${resp.status}): ${detail}`, resp.status);
      }
      this.session = await resp.json();
      this.session._mode = mode;
      return this.session;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err instanceof ApiError) throw err;
      if (err.name === 'AbortError') {
        throw new ApiError('timeout', `Session timeout after ${SESSION_TIMEOUT_MS}ms`);
      }
      throw new ApiError('network', `Network error: ${err.message}`);
    }
  }

  async _safeReadError(resp) {
    try {
      const ct = resp.headers.get('content-type') || '';
      if (ct.includes('json')) {
        const j = await resp.json();
        return j.error || j.detail || JSON.stringify(j).slice(0, 100);
      }
      const txt = await resp.text();
      return txt.slice(0, 100);
    } catch {
      return 'no detail';
    }
  }

  openWebSocket(session, callbacks) {
    this.callbacks = callbacks || {};
    return this._connectWebSocket(session, /*isReconnect=*/false);
  }

  /**
   * Gap 2 fix Sprint Day 44 — Connect WS với reconnect logic.
   *
   * Reconnect strategy:
   *   - 3 retries với exponential backoff (1s, 3s, 7s)
   *   - Chỉ reconnect khi unintentional close (code !== 1000) + chưa nhận final
   *   - Onclose 1006 (abnormal) hoặc 1011 (server error) → trigger reconnect
   *   - Onclose 1000 (normal) hoặc 4xxx (auth) → KHÔNG reconnect, surface error
   */
  _connectWebSocket(session, isReconnect = false) {
    return new Promise((resolve, reject) => {
      const url = `${this.wsBase}/ws/stream`;
      this.ws = new WebSocket(url);
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = () => {
        this._reconnectAttempt = 0;  // reset on successful connect
        this.ws.send(
          JSON.stringify({
            type: 'init',
            access_token: session.access_token,
            trace_id: session.trace_id,
            text: document.getElementById('target-text').textContent,
            audio: { sample_rate: 16000, channels: 1 },
          }),
        );
        if (isReconnect && this.callbacks.onReconnected) {
          this.callbacks.onReconnected();
        }
        resolve();
      };

      this.ws.onmessage = (evt) => {
        if (typeof evt.data !== 'string') return;
        let payload;
        try { payload = JSON.parse(evt.data); } catch { return; }
        if (payload.type === 'ready' && this.callbacks.onReady) {
          this.callbacks.onReady(payload);
        } else if (payload.type === 'final' && this.callbacks.onFinal) {
          this._intentionalClose = true;  // final received → no reconnect needed
          this.callbacks.onFinal(payload.result || payload);
        } else if (payload.type === 'error' && this.callbacks.onError) {
          this.callbacks.onError(new ApiError('server', payload.message || 'ws server error'));
        }
      };

      this.ws.onerror = () => {
        if (this.callbacks.onError) {
          this.callbacks.onError(new ApiError('network', 'WS connection error'));
        }
        if (!isReconnect) reject(new ApiError('network', 'WS error'));
      };

      this.ws.onclose = (evt) => {
        if (this._intentionalClose || evt.code === 1000) {
          return;  // normal close, no reconnect
        }
        if (evt.code >= 4000 && evt.code < 4100) {
          // Auth-level close (engine custom 4xxx codes for token expired/invalid)
          if (this.callbacks.onError) {
            this.callbacks.onError(new ApiError('auth', `WS auth close ${evt.code}: ${evt.reason}`, evt.code));
          }
          return;
        }
        // Try reconnect if attempts remaining
        if (this._reconnectAttempt < WS_RECONNECT_DELAYS_MS.length) {
          const delay = WS_RECONNECT_DELAYS_MS[this._reconnectAttempt];
          this._reconnectAttempt += 1;
          if (this.callbacks.onReconnecting) {
            this.callbacks.onReconnecting(this._reconnectAttempt, delay);
          }
          this._reconnectTimer = setTimeout(() => {
            this._connectWebSocket(session, /*isReconnect=*/true).catch(() => {
              // Reconnect attempt failed — onerror handler will trigger another retry
            });
          }, delay);
        } else {
          // Exhausted retries
          if (this.callbacks.onError) {
            this.callbacks.onError(new ApiError(
              'network',
              `WS closed ${evt.code} after ${WS_RECONNECT_DELAYS_MS.length} reconnect attempts`,
              evt.code,
            ));
          }
        }
      };
    });
  }

  sendAudio(arrayBuffer) {
    if (this.isOpen) this.ws.send(arrayBuffer);
  }

  close() {
    this._intentionalClose = true;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) {
      this.ws.close(1000, 'client done');
    }
  }
}
